/* ─────────────────────────────────────────────────────────────────────────────
   NORMALISER UN NUMÉRO DE TÉLÉPHONE — une seule source, deux usages.

   Ce fichier est chargé par sections/onix-sms-popup.liquid (navigateur) ET
   exécutable en banc d'essai :

       node theme-brouillon/assets/onix-telephone.js --test

   ⛔ LE BANC NE TOURNE JAMAIS À L'IMPORT. Il est derrière `process.argv`, qui
      n'existe pas dans un navigateur. C'est la faute du commit e6501d4 (« le
      test devise agissait à l'import ») : un banc qui s'exécute tout seul finit
      par tourner chez le client.

   POURQUOI PAS UNE BIBLIOTHÈQUE. libphonenumber pèse ~250 ko compressés pour
   une pop-up qui s'ouvre sur chaque page. On valide donc les 13 pays où la
   boutique vend, et on retombe sur la forme E.164 générique pour tout le reste
   — un numéro hors table n'est jamais REFUSÉ, il est juste moins contrôlé.

   ⚠️ LES TROIS PIÈGES PAYÉS, chacun a son cas figé dans le banc :
     1. LE 0 INITIAL N'EST PAS TOUJOURS UN PRÉFIXE À RETIRER. En Italie il fait
        PARTIE du numéro : « +39 06 1234567 » est un fixe de Rome, retirer le 0
        fabrique un numéro qui n'existe pas. Même chose en Espagne, au Portugal,
        au Luxembourg et à Monaco, qui n'ont pas de préfixe interurbain du tout.
     2. LE 0 PEUT ARRIVER APRÈS L'INDICATIF. « +33 (0)6 12 34 56 78 » est la
        forme la plus courante sur les cartes de visite françaises. On retire ce
        0 seulement si le pays a un préfixe interurbain ET si la longueur le
        réclame.
     3. LES ESPACES NE SONT PAS TOUS DES ESPACES. Un copier-coller depuis un
        contact iOS ramène des espaces insécables (U+00A0) et fines (U+202F).
        Un `replace(/ /g,'')` les laisse passer et la validation échoue sur un
        numéro parfaitement valide.

   ⛔ ON REFUSE DES FIXES CONNUS — ON N'EXIGE PAS DE RECONNAÎTRE UN MOBILE.
      Sa consigne du 05/09 : « pour moi c'est pas dérangeant d'avoir cette limite
      parce que dans tous les cas ils nous servent à rien mais faut juste pas que
      tu bloques des numéros que tu crois fixes juste parce que tu t'es basé sur
      la France ». La version d'avant portait un motif `mobile` en LISTE BLANCHE :
      tout préfixe qui n'y entrait pas était refusé, donc tout bloc mobile
      étranger que je n'avais pas pensé à lister. Deux faux blocages MESURÉS sur
      de vrais mobiles, tous deux dans le banc :
        • `+41 74 123 45 67` — le motif suisse s'arrêtait à 75-79, le bloc 074 est
          mobile depuis son attribution par l'OFCOM ;
        • `+43 664 123456` — mobile autrichien à 9 chiffres nationaux, la table
          n'acceptait qu'à partir de 10.
      ➡ La règle est inversée : `fixes` est une LISTE NOIRE par pays, alimentée
        par le plan de numérotation du régulateur (source en commentaire sur
        chaque ligne). Un préfixe ABSENT de la liste passe. Un pays sans règle
        sûre porte `fixes: null` et laisse tout passer.
      ➡ Zéro faux blocage passe avant la pureté de la table : dans le doute, on
        n'écrit pas la ligne.
   ───────────────────────────────────────────────────────────────────────────── */
(function (racine) {
  'use strict';

  /* ── LA TABLE PAR PAYS ──────────────────────────────────────────
     `trunk`     le pays a un préfixe interurbain 0 qui ne fait pas partie du
                 numéro international.
     `longueurs` longueurs NATIONALES plausibles, prises LARGES : une longueur
                 trop serrée refuse un vrai mobile (c'est ce qui bloquait
                 l'Autriche).
     `fixes`     liste NOIRE des préfixes nationaux dont on est sûr qu'ils ne
                 reçoivent pas de message dans CE pays. Absent de la liste =
                 accepté. `null` = pays sans règle sûre, tout passe.
     ⛔ UN PRÉFIXE PARTAGÉ ENTRE FIXE ET MOBILE NE SE MET JAMAIS DANS LA LISTE
        TEL QUEL. En Belgique le 4 est à la fois Liège (fixe, 8 chiffres) et les
        mobiles 04xx (9 chiffres) : c'est la LONGUEUR qui tranche, et le motif
        l'ancre (`4\d{7}$`). Écrire `/^4/` aurait bloqué tous les mobiles belges.
     ⚠️ Les fixes se refusent par le PRÉFIXE, jamais « par défaut » : un bloc
        récemment attribué à un opérateur mobile doit pouvoir s'inscrire le jour
        de son ouverture, sans que je touche ce fichier. */
  var PAYS = {
    /* ARCEP — 06/07 sont les SEULS blocs mobiles. 01→05 fixes géographiques,
       08 services payants, 09 non géographique (VoIP fixe). */
    FR: { indicatif: '33',  trunk: true,  longueurs: [9],
          fixes: /^[1-589]/,            source: 'ARCEP, plan national FR',    nom: 'France' },

    /* IBPT/BIPT — mobiles 045x→049x, 9 chiffres nationaux. Fixes : 2 Bruxelles,
       3 Anvers, 4 Liège, 9 Gand, 1x/5x/6x/7x/8x zones — tous à 8 chiffres.
       Le 4 n'est refusé QUE dans sa forme courte (Liège). */
    BE: { indicatif: '32',  trunk: true,  longueurs: [8, 9],
          fixes: /^(?:[1235-9]|4\d{7}$)/, source: 'IBPT, plan national BE',    nom: 'Belgique' },

    /* OFCOM — mobiles 074→079 (074 inclus, il manquait). Fixes : 021 Lausanne,
       022 Genève, 03x Berne, 04x Zurich/Lucerne, 05x, 06x, 071 Saint-Gall,
       081 Coire, 091 Tessin ; 0800/0900 services. 070/072/073 : non attribués,
       donc laissés passer plutôt que devinés. */
    CH: { indicatif: '41',  trunk: true,  longueurs: [9],
          fixes: /^(?:[2-6]|71|8|9)/,   source: 'OFCOM, plan national CH',     nom: 'Suisse' },

    /* ILR — mobiles 6xx (621→691). Fixes 2x/3x/4x/5x/7x, services 8xx.
       Pas de préfixe interurbain : le 0 fait partie du numéro. */
    LU: { indicatif: '352', trunk: false, longueurs: [8, 9],
          fixes: /^[2-578]/,            source: 'ILR, plan national LU',       nom: 'Luxembourg' },

    /* Monaco Télécom — mobiles 4x et 6x à NEUF chiffres (6 XX XX XX XX), fixes
       9x (93/97/99) à HUIT. N'accepter que 8 refusait tous les mobiles
       monégasques : c'est le même faux blocage que l'Autriche. */
    MC: { indicatif: '377', trunk: false, longueurs: [8, 9],
          fixes: /^9/,                  source: 'plan national MC',            nom: 'Monaco' },

    /* BNetzA — TOUS les indicatifs géographiques commencent par 2→9 ; le bloc 1
       porte les mobiles 015x/016x/017x. On refuse donc 2→9 et on laisse le 1
       entier : un nouveau bloc 018x mobile passerait sans retoucher ici. */
    DE: { indicatif: '49',  trunk: true,  longueurs: [10, 11, 12],
          fixes: /^[2-9]/,              source: 'BNetzA, plan national DE',    nom: 'Allemagne' },

    /* CNMC — mobiles 6xx ET 7xx (le 7 depuis 2010), fixes 8xx/9xx. */
    ES: { indicatif: '34',  trunk: false, longueurs: [9],
          fixes: /^[89]/,               source: 'CNMC, plan national ES',      nom: 'Espagne' },

    /* AGCOM — mobiles 3xx, fixes 0xx où le 0 FAIT PARTIE du numéro. */
    IT: { indicatif: '39',  trunk: false, longueurs: [9, 10],
          fixes: /^0/,                  source: 'AGCOM, plan national IT',     nom: 'Italie' },

    /* ACM — mobiles 6x ; fixes zones 01x→07x ; 084/087/088 VoIP, 0900 services. */
    NL: { indicatif: '31',  trunk: true,  longueurs: [9],
          fixes: /^[1-57-9]/,           source: 'ACM, plan national NL',       nom: 'Pays-Bas' },

    /* ANACOM — le bloc 9 est RÉSERVÉ au mobile ; on l'accepte donc EN ENTIER
       (91/92/93/96 aujourd'hui, 94/95 demain). Fixes 2xx, nomade 30x,
       services 70x/80x. */
    PT: { indicatif: '351', trunk: false, longueurs: [9],
          fixes: /^[2378]/,             source: 'ANACOM, plan national PT',    nom: 'Portugal' },

    /* RTR — mobiles 06xx (650→699), longueur NATIONALE variable de 9 à 13.
       Fixes 1 Vienne, 2xx→7xx zones, 0800/0900 services. */
    AT: { indicatif: '43',  trunk: true,  longueurs: [9, 10, 11, 12, 13],
          fixes: /^[1-57-9]/,           source: 'RTR, plan national AT',       nom: 'Autriche' },

    /* ComReg — mobiles 08x (083/085/086/087/089) ; on accepte le 8 entier.
       Fixes 01 Dublin, 02x→07x zones, 09x. */
    IE: { indicatif: '353', trunk: true,  longueurs: [8, 9],
          fixes: /^[1-79]/,             source: 'ComReg, plan national IE',    nom: 'Irlande' },

    /* Ofcom — mobiles 07x (071→075, 077→079, et 07624 île de Man). Fixes 01/02
       géographiques, 03 non géo, 05 VoIP, 08/09 services. 070 (personal
       numbering) et 076 (pagers) sont AMBIGUS : ils passent. */
    GB: { indicatif: '44',  trunk: true,  longueurs: [10],
          fixes: /^[1-3589]/,           source: 'Ofcom, plan national GB',     nom: 'Royaume-Uni' }
  };

  /* Ordre de recherche : l'indicatif LE PLUS LONG d'abord. Sans ça, « +352… »
     serait lu comme « +35 » puis « 2… », et « +377… » comme « +37 ». */
  var CODES = Object.keys(PAYS).sort(function (a, b) {
    return PAYS[b].indicatif.length - PAYS[a].indicatif.length;
  });

  /* E.164 : « + », un premier chiffre non nul, puis 7 à 14 chiffres. C'est la
     seule règle appliquée aux pays hors table. */
  var E164 = /^\+[1-9]\d{7,14}$/;

  function nettoyer(brut) {
    return String(brut == null ? '' : brut)
      /* espaces sous toutes leurs formes, y compris insécable et fine */
      .replace(/[\s    ]/g, '')
      /* ponctuation de mise en forme */
      .replace(/[().–—/\\-]/g, '')
      .trim();
  }

  /* normaliser(brut, paysParDefaut) → { ok, e164, pays, raison }
     `paysParDefaut` = le pays choisi dans le sélecteur. Un indicatif écrit en
     clair dans le champ l'emporte TOUJOURS sur le sélecteur : quelqu'un qui
     tape « +41 79… » sait mieux que nous d'où il appelle. */
  function normaliser(brut, paysParDefaut) {
    var s = nettoyer(brut);
    if (!s) return { ok: false, raison: 'vide' };

    /* 00 est la forme internationale écrite ; + est la forme canonique. */
    if (s.indexOf('00') === 0) s = '+' + s.slice(2);

    /* Un signe + ailleurs qu'en tête, ou un caractère non chiffré : refus net. */
    if (!/^\+?\d+$/.test(s)) return { ok: false, raison: 'format' };

    var pays = null;
    var national = null;

    if (s.charAt(0) === '+') {
      var chiffres = s.slice(1);
      for (var i = 0; i < CODES.length; i++) {
        var code = CODES[i];
        if (chiffres.indexOf(PAYS[code].indicatif) === 0) {
          pays = code;
          national = chiffres.slice(PAYS[code].indicatif.length);
          break;
        }
      }
      if (!pays) {
        /* Pays hors table (DOM, reste du monde) : seule la forme E.164 est
           contrôlée. On n'invente pas de règle nationale qu'on ne connaît pas.
           ⚠️ LIMITE ASSUMÉE, mesurée par le banc : un indicatif NON ATTRIBUÉ par
              l'UIT (« +999 ») passe cette branche — E.164 ne contraint que la
              forme, pas l'attribution. Refuser demanderait la table complète des
              indicatifs mondiaux ; le Worker, lui, revérifie côté serveur. */
        if (E164.test(s)) return { ok: true, e164: s, pays: null };
        return { ok: false, raison: /^\+0/.test(s) ? 'format' : 'longueur' };
      }
    } else {
      pays = (paysParDefaut || '').toUpperCase();
      if (!PAYS[pays]) return { ok: false, raison: 'indicatif_inconnu' };
      national = s;
    }

    var def = PAYS[pays];

    /* PIÈGE 1 & 2 : on ne retire le 0 que si le pays en a un, et seulement
       quand la longueur montre qu'il est en trop. */
    if (def.trunk && national.charAt(0) === '0') {
      var sansZero = national.slice(1);
      for (var j = 0; j < def.longueurs.length; j++) {
        if (sansZero.length === def.longueurs[j]) { national = sansZero; break; }
      }
    }

    if (def.longueurs.indexOf(national.length) === -1) {
      return { ok: false, raison: 'longueur', pays: pays };
    }
    /* ⛔ LE SENS DU TEST EST L'INVERSE DE L'ANCIEN. On ne demande plus au
       numéro de PROUVER qu'il est mobile — on refuse seulement ce qu'on sait
       être un fixe DANS CE PAYS. Un pays sans règle sûre (`fixes: null`) ou un
       préfixe absent de la liste passe. */
    if (def.fixes && def.fixes.test(national)) {
      return { ok: false, raison: 'pas_mobile', pays: pays };
    }

    var e164 = '+' + def.indicatif + national;
    if (!E164.test(e164)) return { ok: false, raison: 'format', pays: pays };
    return { ok: true, e164: e164, pays: pays };
  }

  racine.OnixTelephone = { normaliser: normaliser, PAYS: PAYS, CODES: CODES };

  /* ── BANC D'ESSAI ────────────────────────────────────────
     TROIS FAMILLES, ET LES TROIS COMPTENT :
       1. MOBILES ÉTRANGERS qui doivent PASSER — c'est la famille que l'ancienne
          liste blanche cassait, et c'est le critère n°1 : zéro faux blocage ;
       2. FIXES SÛRS qui doivent Être REFUSÉS, dans plusieurs pays et pas
          seulement en France ;
       3. AMBIGUS qui doivent PASSER — bloc non attribué, pays hors table,
          numérotation personnelle. Sans cette famille, resserrer la table au
          hasard passerait le banc.
     ⚠ Chaque cas porte son motif en clair : un banc dont on ne sait plus ce
        qu'il prouve se fait élaguer à la première gêne. */
  function banc() {
    var passe = [
      /* ── la France, telle qu'elle était déjà couverte ── */
      ['06 12 34 56 78',        'FR', '+33612345678',    'mobile FR écrit à la française'],
      ['0612345678',            'FR', '+33612345678',    'mobile FR collé'],
      ['+33 6 12 34 56 78',     'FR', '+33612345678',    'indicatif en clair'],
      ['0033612345678',         'FR', '+33612345678',    'forme 00'],
      ['+33 (0)6 12 34 56 78',  'FR', '+33612345678',    'le 0 entre parenthèses saute'],
      ['07 55 12 34 56',        'FR', '+33755123456',    'le bloc 07 est mobile aussi'],
      /* ── MOBILES ÉTRANGERS — la famille que la liste blanche cassait ── */
      ['+41 79 123 45 67',      'FR', '+41791234567',    'CH 079, l’indicatif tapé bat le sélecteur'],
      ['074 123 45 67',         'CH', '+41741234567',    'CH 074 — REFUSÉ par l’ancien motif 7[5-9]'],
      ['0470 12 34 56',         'BE', '+32470123456',    'BE 047x, 9 chiffres'],
      ['+32 456 12 34 56',      'FR', '+32456123456',    'BE 045x'],
      ['612 34 56 78',          'ES', '+34612345678',    'ES 6xx'],
      ['712345678',             'ES', '+34712345678',    'ES 7xx, mobile depuis 2010'],
      ['+39 320 123 4567',      'FR', '+393201234567',   'IT 3xx, aucun 0 à retirer'],
      ['06 12345678',           'NL', '+31612345678',    'NL 06'],
      ['+352 621 123 456',      'FR', '+352621123456',   'LU 6xx'],
      ['\u00A007 700 900123',     'GB', '+447700900123',   'GB 07xxx, espace insécable en tête'],
      ['+49 151 23456789',      'FR', '+4915123456789',  'DE 15x'],
      ['0176 12345678',         'DE', '+4917612345678',  'DE 17x'],
      ['0664 123456',           'AT', '+43664123456',    'AT 664 à 9 chiffres — REFUSÉ par l’ancienne longueur'],
      ['+43 660 1234567',       'FR', '+436601234567',   'AT 660 à 10 chiffres'],
      ['+353 89 123 4567',      'FR', '+353891234567',   'IE 089'],
      ['+351 940 123 456',      'FR', '+351940123456',   'PT 94x — REFUSÉ par l’ancien motif 9[1236]'],
      ['+377 6 78 12 34 56',    'FR', '+377678123456',   'MC 6x à 9 chiffres — REFUSÉ par l’ancienne longueur'],
      /* ── AMBIGUS : on ne devine pas, on laisse passer ── */
      ['+44 7624 123456',       'FR', '+447624123456',   'GB 07624, île de Man'],
      ['+44 7000 123456',       'FR', '+447000123456',   'GB 070, numérotation personnelle — ambigu'],
      ['+41 70 123 45 67',      'FR', '+41701234567',    'CH 070, bloc non attribué — ambigu'],
      ['+1 415 555 0132',       'FR', '+14155550132',    'hors table — États-Unis'],
      ['+212 612345678',        'FR', '+212612345678',   'hors table — Maroc'],
      ['+971 50 123 4567',      'FR', '+971501234567',   'hors table — Émirats']
    ];
    var echoue = [
      /* ── FIXES SÛRS, ET PAS QU'EN FRANCE ── */
      ['01 42 68 53 00',        'FR', 'pas_mobile', 'FR 01 — fixe Île-de-France'],
      ['+33 1 42 68 53 00',     'FR', 'pas_mobile', 'FR 01 en international'],
      ['+33 4 91 12 34 56',     'FR', 'pas_mobile', 'FR 04 — fixe Marseille'],
      ['+33 8 92 70 12 39',     'FR', 'pas_mobile', 'FR 08 — service payant'],
      ['+32 2 123 45 67',       'FR', 'pas_mobile', 'BE 02 — fixe Bruxelles'],
      ['+32 4 223 45 67',       'FR', 'pas_mobile', 'BE 04 COURT — fixe Liège, 8 chiffres'],
      ['+41 44 123 45 67',      'FR', 'pas_mobile', 'CH 044 — fixe Zurich'],
      ['+41 71 123 45 67',      'FR', 'pas_mobile', 'CH 071 — fixe Saint-Gall, dans le bloc 7'],
      ['+39 06 1234567',        'FR', 'pas_mobile', 'IT 06 — fixe Rome'],
      ['+34 912 345 678',       'FR', 'pas_mobile', 'ES 91 — fixe Madrid'],
      ['+49 30 12345678',       'FR', 'pas_mobile', 'DE 30 — fixe Berlin'],
      ['+44 20 7123 4567',      'FR', 'pas_mobile', 'GB 020 — fixe Londres'],
      ['+31 20 123 4567',       'FR', 'pas_mobile', 'NL 020 — fixe Amsterdam'],
      ['+351 21 123 4567',      'FR', 'pas_mobile', 'PT 21 — fixe Lisbonne'],
      ['+43 1 2345 6789',       'FR', 'pas_mobile', 'AT 1 — fixe Vienne'],
      ['+353 1 234 5678',       'FR', 'pas_mobile', 'IE 01 — fixe Dublin'],
      ['+352 26 12 34 56',      'FR', 'pas_mobile', 'LU 26 — fixe'],
      ['+377 93 15 12 34',      'FR', 'pas_mobile', 'MC 93 — fixe'],
      /* ── formes invalides ── */
      ['',                      'FR', 'vide',       'champ vide'],
      ['   ',                   'FR', 'vide',       'espaces seuls'],
      ['06 12 34',              'FR', 'longueur',   'trop court'],
      ['+33 6 12 34 56 78 90',  'FR', 'longueur',   'trop long'],
      ['abcdefghij',            'FR', 'format',     'des lettres'],
      ['06 12 34 56 7A',        'FR', 'format',     'une lettre au milieu'],
      ['+1 415 555',            'FR', 'longueur',   'hors table, trop court pour E.164'],
      ['+0 6 12 34 56 78',      'FR', 'format',     'indicatif commençant par 0'],
      ['0612345678',            'XX', 'indicatif_inconnu', 'pays hors sélecteur, sans indicatif']
    ];

    var rates = 0;
    passe.forEach(function (c) {
      var r = normaliser(c[0], c[1]);
      if (!r.ok || r.e164 !== c[2]) {
        rates++;
        console.log('\u26d4 DOIT PASSER  — ' + JSON.stringify(c[0]) + ' (' + c[1] + ') [' + c[3] + '] → attendu '
          + c[2] + ', obtenu ' + (r.ok ? r.e164 : 'refus:' + r.raison));
      }
    });
    echoue.forEach(function (c) {
      var r = normaliser(c[0], c[1]);
      if (r.ok || r.raison !== c[2]) {
        rates++;
        console.log('\u26d4 DOIT REFUSER — ' + JSON.stringify(c[0]) + ' (' + c[1] + ') [' + c[3] + '] → attendu refus:'
          + c[2] + ', obtenu ' + (r.ok ? 'ACCEPTÉ ' + r.e164 : 'refus:' + r.raison));
      }
    });

    /* Ce que la table refuse, pays par pays — imprimé, pas affirmé. */
    console.log('  table par pays : ' + CODES.slice().sort().map(function (k) {
      return k + '=' + (PAYS[k].fixes ? String(PAYS[k].fixes) : 'rien');
    }).join(' '));

    var total = passe.length + echoue.length;
    console.log((total - rates) + '/' + total + ' cas — ' + passe.length + ' doivent passer ('
      + 'dont mobiles étrangers et ambigus), ' + echoue.length + ' doivent refuser.');
    if (typeof process !== 'undefined' && process.exit) process.exit(rates ? 2 : 0);
  }

  if (typeof process !== 'undefined' && process.argv && process.argv.indexOf('--test') !== -1) banc();
})(typeof globalThis !== 'undefined' ? globalThis : this);
